import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/** A toolbar icon button (`size-6`) and the `gap-0.5` between buttons. */
export const ICON_PX = 24;
export const ICON_GAP_PX = 2;
/** `PaneHeader`: `px-3` on both sides and the `gap-2` before the actions. */
const HEADER_CHROME_PX = 12 + 12 + 8;
/**
 * How much wider than strictly needed the header must get before a hidden
 * action comes back, so dragging across a boundary doesn't make it blink.
 */
export const HYSTERESIS_PX = 8;

export type FitInput<Id extends string> = {
  /** Most important first; the last ones move to the menu first. */
  actions: { id: Id; width: number }[];
  /** Room for the movable actions (the fixed ones already taken out). */
  available: number;
  gap?: number;
  /** How many fit last time, for the hysteresis. */
  previous?: number;
  hysteresis?: number;
};

export type FitResult<Id extends string> = {
  visible: Set<Id>;
  /** Overflowed actions, most important first. */
  overflow: Id[];
};

/**
 * Priority+: keep as many of the top-priority actions as fit in `available`,
 * one by one; the rest go to the "…" menu. Each shown action costs its width
 * plus one gap. Showing more than last time needs `hysteresis` px to spare.
 */
export const fitActions = <Id extends string>({
  actions,
  available,
  gap = ICON_GAP_PX,
  previous,
  hysteresis = HYSTERESIS_PX
}: FitInput<Id>): FitResult<Id> => {
  let used = 0;
  let count = 0;
  for (const action of actions) {
    const next = used + action.width + gap;
    const slack = previous !== undefined && count >= previous ? hysteresis : 0;
    if (next + slack > available) break;
    used = next;
    count += 1;
  }
  return {
    visible: new Set(actions.slice(0, count).map((a) => a.id)),
    overflow: actions.slice(count).map((a) => a.id)
  };
};

type FitOptions<Id extends string> = {
  /**
   * The unpadded box around the `PaneHeader`: its width doesn't depend on how
   * many actions show. The title (and its chip) keeps the room it needs.
   */
  headerRef: RefObject<HTMLElement | null>;
  priority: readonly Id[];
  /** Buttons that never move to the menu ("…", Maximize). */
  fixedCount: number;
  /** Re-measure the title when this changes (a chip came or went). */
  titleKey?: unknown;
};

/** The title's natural width: truncation hides it from `offsetWidth`. */
const naturalWidth = (el: HTMLElement | null) => {
  if (!el) return 0;
  const parts = [...el.children] as HTMLElement[];
  if (!parts.length) return el.scrollWidth;
  const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
  return (
    parts.reduce((sum, part) => sum + part.scrollWidth, 0) +
    gap * (parts.length - 1)
  );
};

/**
 * Which header actions fit, from a ResizeObserver on the header. Until the
 * header has been measured (and in jsdom) everything shows.
 */
export const useHeaderFit = <Id extends string>({
  headerRef,
  priority,
  fixedCount,
  titleKey
}: FitOptions<Id>): FitResult<Id> => {
  const all = (): FitResult<Id> => ({
    visible: new Set(priority),
    overflow: []
  });
  const [fit, setFit] = useState<FitResult<Id>>(all);
  const previous = useRef<number | undefined>(undefined);
  const key = priority.join(",");

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;
    const measure = (width: number) => {
      if (width <= 0) return;
      const fixed = fixedCount * ICON_PX + (fixedCount - 1) * ICON_GAP_PX;
      const available =
        width -
        HEADER_CHROME_PX -
        naturalWidth(
          header.querySelector<HTMLElement>("[data-slot=pane-header] > span")
        ) -
        fixed;
      const next = fitActions({
        actions: priority.map((id) => ({ id, width: ICON_PX })),
        available,
        previous: previous.current
      });
      previous.current = next.visible.size;
      setFit((cur) =>
        cur.overflow.join(",") === next.overflow.join(",") ? cur : next
      );
    };
    measure(header.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) measure(entry.contentRect.width);
    });
    observer.observe(header);
    return () => observer.disconnect();
    // `priority` is compared by `key`.
  }, [headerRef, key, fixedCount, titleKey]);

  return fit;
};
